import React, { useState, useEffect } from 'react';
import { CheckBadgeIcon, XCircleIcon } from '@heroicons/react/24/outline';
import subscriptionService from '../services/subscriptionService';

function SubscriptionManagement() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const data = await subscriptionService.getSubscriptionInfo();
      setSubscription(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      await subscriptionService.createBillingPortal();
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      trialing: { color: 'bg-blue-100 text-blue-800', label: 'Trial' },
      past_due: { color: 'bg-yellow-100 text-yellow-800', label: 'Past Due' },
      canceled: { color: 'bg-red-100 text-red-800', label: 'Canceled' },
      unpaid: { color: 'bg-red-100 text-red-800', label: 'Unpaid' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  if (!subscription || subscription.status === 'free') {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <XCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Subscription</h2>
            <p className="text-gray-600 mb-6">
              You don't have an active subscription. Upgrade to access premium features.
            </p>
            <button
              onClick={async () => {
                window.location.href = '/pricing';
              }}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Simplified subscription info
  const {
    status,
    tier,
    is_trial,
    trial_days_remaining,
    current_period_end,
    cancel_at_period_end,
    payment_action_required,
  } = subscription;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Subscription Management</h1>

        {/* Subscription Overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Subscription Overview</h2>
            {getStatusBadge(status)}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Status</p>
              <p className="font-semibold text-gray-900 capitalize">
                {status === 'trialing' ? 'Free Trial' : status}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Tier</p>
              <p className="font-semibold text-gray-900 capitalize">
                {tier}
              </p>
            </div>

            {current_period_end && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Next Billing Date</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(current_period_end)}
                </p>
              </div>
            )}
          </div>

          {is_trial && trial_days_remaining && trial_days_remaining > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                <CheckBadgeIcon className="inline h-5 w-5 mr-1" />
                {trial_days_remaining} days remaining in your free trial
              </p>
            </div>
          )}

          {cancel_at_period_end && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800">
                <XCircleIcon className="inline h-5 w-5 mr-1" />
                Cancellation is scheduled. Access ends on {current_period_end ? formatDate(current_period_end) : 'your next billing date'}.
              </p>
            </div>
          )}

          {status === 'canceled' && !cancel_at_period_end && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">
                <XCircleIcon className="inline h-5 w-5 mr-1" />
                Your subscription has been canceled
              </p>
            </div>
          )}
          {(status === 'past_due' || status === 'unpaid' || payment_action_required) && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-900">
                Billing needs your attention. Open the secure Stripe portal to update your payment method.
              </p>
            </div>
          )}
        </div>


        {/* Actions */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions</h2>
          <div className="space-y-3">
            <button
              onClick={handleManageBilling}
              className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Manage billing, payment methods, or cancellation
            </button>
            <p className="text-sm text-gray-500">
              Stripe securely handles card updates, invoices, and cancellation. Cancellation timing is shown before you confirm.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

export default SubscriptionManagement;
